import { useEffect, useState } from 'react';
import { api, type GrowthRow, type MonthCloseProjection, type Sede } from '../lib/api.js';
import { useFilters, sedesParam } from '../lib/filters.js';
import { Filters } from '../components/Filters.js';
import { ChartCard } from '../components/charts/ChartCard.js';

function pct(v: number | null | undefined): string {
  if (v == null) return '—';
  const s = v > 0 ? '+' : '';
  return `${s}${(v * 100).toFixed(1)}%`;
}
function tone(v: number | null | undefined): string {
  if (v == null || v === 0) return 'text-slate-400';
  return v > 0 ? 'text-emerald-400' : 'text-rose-400';
}
function num(v: number | null | undefined): string {
  return v == null ? '—' : Math.round(v).toLocaleString('es-VE');
}

const METRIC_LABEL: Record<string, string> = {
  roomRevenueBs: 'Room Revenue (Bs)',
  totalRevenueBs: 'Total Revenue (Bs)',
  ocupadasTotales: 'Noches ocupadas',
};

export function Crecimiento() {
  const [filters] = useFilters();
  const [sedes, setSedes] = useState<Sede[]>([]);
  const [growth, setGrowth] = useState<GrowthRow[]>([]);
  const [projections, setProjections] = useState<MonthCloseProjection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .sedes()
      .then((r) => setSedes(r.sedes))
      .catch(() => setSedes([]));
  }, []);

  const anio = Number(filters.to.slice(0, 4));
  const mes = Number(filters.to.slice(5, 7));
  const key = `${anio}|${mes}|${filters.sedes.join(',')}`;
  useEffect(() => {
    const sedesP = sedesParam(filters);
    setLoading(true);
    setError(null);
    Promise.all([
      api.growth({ anio, sedes: sedesP }),
      api.projectionsMonth({ anio, mes, sedes: sedesP }),
    ])
      .then(([g, p]) => {
        setGrowth(g.rows);
        setProjections(p.projections);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <div className="space-y-5">
      <Filters sedes={sedes} />
      {loading && <p className="text-slate-400">Cargando…</p>}
      {error && <p className="rounded-lg bg-rose-950/60 px-3 py-2 text-rose-300">{error}</p>}

      <ChartCard title={`Crecimiento interanual por sede (YTD ${anio} vs ${anio - 1})`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="px-2 py-1">Sede</th>
                <th className="px-2 py-1">Ocupación</th>
                <th className="px-2 py-1">ADR</th>
                <th className="px-2 py-1">RevPAR</th>
                <th className="px-2 py-1">Ingreso USD</th>
                <th className="px-2 py-1">Diamond</th>
              </tr>
            </thead>
            <tbody>
              {growth.map((r) => (
                <tr key={r.sedeId} className="border-t border-slate-800">
                  <td className="px-2 py-1.5 font-mono text-blue-400">{r.codigo}</td>
                  <td className={`px-2 py-1.5 ${tone(r.occ.growth)}`}>{pct(r.occ.growth)}</td>
                  <td className={`px-2 py-1.5 ${tone(r.adrUsd.growth)}`}>{pct(r.adrUsd.growth)}</td>
                  <td className={`px-2 py-1.5 ${tone(r.revparUsd.growth)}`}>
                    {pct(r.revparUsd.growth)}
                  </td>
                  <td className={`px-2 py-1.5 ${tone(r.totalRevenueUsd.growth)}`}>
                    {pct(r.totalRevenueUsd.growth)}
                  </td>
                  <td className={`px-2 py-1.5 ${tone(r.nochesDiamond.growth)}`}>
                    {pct(r.nochesDiamond.growth)}
                  </td>
                </tr>
              ))}
              {growth.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-2 py-4 text-center text-slate-500">
                    Sin datos.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <div className="grid gap-4 md:grid-cols-3">
        {projections.map((p) => (
          <div key={p.metric} className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
            <h3 className="text-sm font-medium text-slate-200">
              {METRIC_LABEL[p.metric] ?? p.metric}
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Cierre de mes {p.anio}-{String(p.mes).padStart(2, '0')} · día {p.daysElapsed} · MTD{' '}
              {num(p.mtd)}
            </p>
            <dl className="mt-2 space-y-1 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-400">Run-rate</dt>
                <dd className="text-slate-100">{num(p.runRate.value)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-400">Estacional</dt>
                <dd className="text-slate-100">{num(p.seasonal.value)}</dd>
              </div>
              {p.seasonal.bandLow != null && (
                <div className="flex justify-between text-xs">
                  <dt className="text-slate-500">Banda</dt>
                  <dd className="text-slate-400">
                    {num(p.seasonal.bandLow)} – {num(p.seasonal.bandHigh)}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}
